import React, { useEffect, useState } from 'react';

interface VenereAvatarProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'default' | 'welcome' | 'helpful' | 'excited' | 'simple';
  className?: string;
  onClick?: () => void;
  interactive?: boolean;
}

export const VenereAvatar: React.FC<VenereAvatarProps> = ({
  size = 'md',
  variant = 'default',
  className = '',
  onClick,
  interactive = false,
}) => {
  const [isBlinking, setIsBlinking] = useState(false);

  // Blinking animation every 5 seconds
  useEffect(() => {
    const blinkInterval = setInterval(() => {
      setIsBlinking(true);
      setTimeout(() => setIsBlinking(false), 200); // Blink for 200ms
    }, 5000);

    return () => clearInterval(blinkInterval);
  }, []);

  const sizeClasses = {
    sm: 'w-12 h-12',
    md: 'w-16 h-16',
    lg: 'w-20 h-20',
    xl: 'w-24 h-24',
  };

  const baseClasses = 'rounded-full flex items-center justify-center transition-all duration-200';
  const interactiveClasses = interactive || onClick ? 'cursor-pointer hover:scale-105 active:scale-95' : '';

  const getAvatarContent = () => {
    switch (variant) {
      case 'simple':
        return (
          <div 
            className="w-full h-full rounded-full flex items-center justify-center"
            style={{
              background: 'linear-gradient(135deg, var(--venere-royal) 0%, var(--venere-royal) 100%)'
            }}
          >
            {/* Simple eyes only */}
            <div className="flex space-x-1">
              <div 
                className={`w-1.5 h-1.5 rounded-full transition-all duration-150 ${isBlinking ? 'h-0.5' : ''}`}
                style={{ backgroundColor: 'var(--venere-matcha)' }}
              ></div>
              <div 
                className={`w-1.5 h-1.5 rounded-full transition-all duration-150 ${isBlinking ? 'h-0.5' : ''}`}
                style={{ backgroundColor: 'var(--venere-matcha)' }}
              ></div>
            </div>
          </div>
        );
      
      case 'welcome':
        return (
          <div 
            className="w-full h-full rounded-full flex flex-col items-center justify-center relative"
            style={{
              background: 'linear-gradient(135deg, var(--venere-royal) 0%, var(--venere-royal) 100%)'
            }}
          >
            {/* Eyes */}
            <div className="flex space-x-2 mb-2">
              <div 
                className={`w-2 h-2 rounded-full transition-all duration-150 ${isBlinking ? 'h-0.5' : ''}`}
                style={{ backgroundColor: 'var(--venere-matcha)' }}
              ></div>
              <div 
                className={`w-2 h-2 rounded-full transition-all duration-150 ${isBlinking ? 'h-0.5' : ''}`}
                style={{ backgroundColor: 'var(--venere-matcha)' }}
              ></div>
            </div>
            {/* Smile */}
            <div 
              className="w-6 h-3 border-b-4 border-solid rounded-full"
              style={{ borderColor: 'var(--venere-matcha)' }}
            ></div>
          </div>
        );
      
      case 'helpful':
        return (
          <div 
            className="w-full h-full rounded-full flex flex-col items-center justify-center relative"
            style={{
              background: 'linear-gradient(135deg, var(--venere-matcha) 0%, var(--venere-sky) 100%)'
            }}
          >
            {/* Eyes */}
            <div className="flex space-x-2 mb-2">
              <div 
                className={`w-2 h-2 rounded-full transition-all duration-150 ${isBlinking ? 'h-0.5' : ''}`}
                style={{ backgroundColor: 'var(--venere-navy)' }}
              ></div>
              <div 
                className={`w-2 h-2 rounded-full transition-all duration-150 ${isBlinking ? 'h-0.5' : ''}`}
                style={{ backgroundColor: 'var(--venere-navy)' }}
              ></div>
            </div>
            {/* Smile */}
            <div 
              className="w-6 h-3 border-b-4 border-solid rounded-full"
              style={{ borderColor: 'var(--venere-navy)' }}
            ></div>
          </div>
        );
      
      case 'excited':
        return (
          <div 
            className="w-full h-full rounded-full flex flex-col items-center justify-center relative"
            style={{
              background: 'linear-gradient(135deg, var(--venere-purple) 0%, var(--venere-lavender) 100%)'
            }}
          >
            {/* Eyes */}
            <div className="flex space-x-2 mb-2">
              <div 
                className={`w-2 h-2 rounded-full transition-all duration-150 ${isBlinking ? 'h-0.5' : ''}`}
                style={{ backgroundColor: 'white' }}
              ></div>
              <div 
                className={`w-2 h-2 rounded-full transition-all duration-150 ${isBlinking ? 'h-0.5' : ''}`}
                style={{ backgroundColor: 'white' }}
              ></div>
            </div>
            {/* Smile */}
            <div 
              className="w-6 h-3 border-b-4 border-solid rounded-full"
              style={{ borderColor: 'white' }}
            ></div>
          </div>
        );
      
      default:
        return (
          <div 
            className="w-full h-full rounded-full flex flex-col items-center justify-center relative"
            style={{
              background: 'linear-gradient(135deg, var(--venere-royal) 0%, var(--venere-navy) 100%)'
            }}
          >
            {/* Eyes */}
            <div className="flex space-x-2 mb-2">
              <div 
                className={`w-2 h-2 rounded-full transition-all duration-150 ${isBlinking ? 'h-0.5' : ''}`}
                style={{ backgroundColor: 'white' }}
              ></div>
              <div 
                className={`w-2 h-2 rounded-full transition-all duration-150 ${isBlinking ? 'h-0.5' : ''}`}
                style={{ backgroundColor: 'white' }}
              ></div>
            </div>
            {/* Smile */}
            <div 
              className="w-6 h-3 border-b-4 border-solid rounded-full"
              style={{ borderColor: 'white' }}
            ></div>
          </div>
        );
    }
  };

  return (
    <div
      className={`${baseClasses} ${sizeClasses[size]} ${interactiveClasses} ${className}`}
      onClick={onClick}
    >
      {getAvatarContent()}
    </div>
  );
};

export default VenereAvatar; 